import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getOrgTokens, ORG_PALETTE } from "../../../theme/style/organizationPalette";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

const ActionableLeversTable = ({
  data,
  categories: propCategories,
  summary: propSummary,
  onSelectLever,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const categories = data?.categories || propCategories || [];
  const summary = data?.summary || propSummary || null;

  if (!categories || categories.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: ORG_PALETTE.radius.card,
          border: `1px solid ${tok.border.card}`,
          bgcolor: tok.bg.card,
          textAlign: "center",
        }}
      >
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {t("No emissions rows available for actionable analysis.")}
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: ORG_PALETTE.radius.card,
        border: `1px solid ${tok.border.card}`,
        bgcolor: tok.bg.card,
        overflow: "hidden",
      }}
    >
      <Table sx={{ minWidth: 850 }}>
        {/* Table Header: All header cells Center Aligned per design guidelines */}
        <TableHead
          sx={{
            bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
            borderBottom: `2px solid ${tok.border.divider}`,
          }}
        >
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: ORG_PALETTE.font.size.gridHeader,
                textAlign: "center",
                color: tok.font.tableTitle,
                width: "22%",
                py: 2,
              }}
            >
              {t("Category (tCO₂e)")}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: ORG_PALETTE.font.size.gridHeader,
                textAlign: "center",
                color: tok.font.tableTitle,
                width: "22%",
                py: 2,
              }}
            >
              {t("Lever")}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: ORG_PALETTE.font.size.gridHeader,
                textAlign: "center",
                color: tok.font.tableTitle,
                width: "28%",
                py: 2,
              }}
            >
              {t("Variable Change (Baseline → Scenario)")}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: ORG_PALETTE.font.size.gridHeader,
                textAlign: "center",
                color: tok.font.tableTitle,
                width: "12%",
                py: 2,
              }}
            >
              {t("% GHG Reduction")}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: ORG_PALETTE.font.size.gridHeader,
                textAlign: "center",
                color: tok.font.tableTitle,
                width: "8%",
                py: 2,
              }}
            >
              {t("Min Reduction")}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: ORG_PALETTE.font.size.gridHeader,
                textAlign: "center",
                color: tok.font.tableTitle,
                width: "8%",
                py: 2,
              }}
            >
              {t("Max Reduction")}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {categories.map((cat, catIdx) => {
            const numLevers = cat.levers.length;
            const hasLevers = numLevers > 0;

            return (
              <React.Fragment key={cat.rowId || `cat-${catIdx}`}>
                {/* Render levers for this category */}
                {hasLevers ? (
                  cat.levers.map((lever, leverIdx) => (
                    <TableRow
                      key={lever.leverId || `lev-${catIdx}-${leverIdx}`}
                      hover
                      sx={{
                        "&:hover": {
                          bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                        },
                        borderBottom: `1px solid ${tok.border.divider}`,
                      }}
                    >
                      {/* Category cell (spanned across all levers + combined row) */}
                      {leverIdx === 0 && (
                        <TableCell
                          rowSpan={numLevers + 1}
                          sx={{
                            verticalAlign: "top",
                            borderRight: `1px solid ${tok.border.divider}`,
                            bgcolor: isDark ? "rgba(255,255,255,0.01)" : "inherit",
                            p: 2.5,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              fontSize: "15px",
                              color: tok.font.tableTitle,
                              textAlign: "left",
                            }}
                          >
                            {`${cat.category} (${(cat.baselineTco2e ?? cat.baseline_tco2e ?? 0).toLocaleString()})`}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              display: "block",
                              mt: 0.5,
                              textAlign: "left",
                            }}
                          >
                            {`${cat.sharePct ?? cat.share_pct ?? 0}% ${t("of total emissions")}`}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontStyle: "italic",
                              display: "block",
                              mt: 0.5,
                              textAlign: "left",
                              fontSize: "11px",
                            }}
                          >
                            {cat.driver}
                          </Typography>
                        </TableCell>
                      )}

                      {/* Lever Name */}
                      <TableCell sx={{ textAlign: "left", py: 1.8 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              fontSize: "14px",
                              color: tok.font.tableTitle,
                            }}
                          >
                            {lever.leverName || lever.lever_name}
                          </Typography>
                          <Tooltip title={t("Inspect Operating Variables & Formula")}>
                            <IconButton
                              size="small"
                              onClick={() => onSelectLever(lever, cat)}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { color: tok.font.accent },
                                ml: 1,
                              }}
                            >
                              <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      {/* Variable Change (Baseline -> Scenario) */}
                      <TableCell sx={{ textAlign: "left", py: 1.8 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "13px",
                            color: tok.font.tableTitle,
                            lineHeight: 1.4,
                          }}
                        >
                          {lever.variableChange || lever.variable_change}
                        </Typography>
                      </TableCell>

                      {/* % GHG Reduction (Numeric: Right Aligned) */}
                      <TableCell sx={{ textAlign: "right", py: 1.8 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "13px",
                            color: isDark ? "#38bdf8" : "#0284c7",
                          }}
                        >
                          {lever.pctReductionStr || lever.pct_reduction_str}
                        </Typography>
                      </TableCell>

                      {/* Min Reduction (Numeric: Right Aligned) */}
                      <TableCell sx={{ textAlign: "right", py: 1.8 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "14px" }}>
                          {lever.minReductionTco2e ?? lever.min_reduction_tco2e ?? 0}
                        </Typography>
                      </TableCell>

                      {/* Max Reduction (Numeric: Right Aligned) */}
                      <TableCell sx={{ textAlign: "right", py: 1.8 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "14px" }}>
                          {lever.maxReductionTco2e ?? lever.max_reduction_tco2e ?? 0}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell sx={{ textAlign: "left", p: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {`${cat.category} (${(cat.baselineTco2e ?? cat.baseline_tco2e ?? 0).toLocaleString()})`}
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={5} sx={{ textAlign: "center", color: "text.secondary" }}>
                      {t("No specific levers identified for this row.")}
                    </TableCell>
                  </TableRow>
                )}

                {/* Category Subtotal / Combined Row */}
                {hasLevers && (
                  <TableRow
                    sx={{
                      bgcolor: isDark ? "rgba(255, 255, 255, 0.05)" : "#e2e8f0",
                      borderBottom: `2px solid ${tok.border.divider}`,
                    }}
                  >
                    <TableCell sx={{ textAlign: "left", py: 1.5, pl: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontSize: "14px",
                          color: tok.font.tableTitle,
                        }}
                      >
                        {t("Combined")}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "left", py: 1.5 }}>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {t("Multi-lever independent interaction")}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "right", py: 1.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontSize: "14px",
                          color: isDark ? "#38bdf8" : "#0369a1",
                        }}
                      >
                        {cat.combined?.pctReductionStr || cat.combined?.pct_reduction_str || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "right", py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "14px" }}>
                        {cat.combined?.minReductionTco2e ?? cat.combined?.min_reduction_tco2e ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "right", py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "14px" }}>
                        {cat.combined?.maxReductionTco2e ?? cat.combined?.max_reduction_tco2e ?? 0}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}

          {/* Overall Summary Row: Total, top 5 (Light green background matching user screenshots) */}
          {summary && (
            <TableRow
              sx={{
                bgcolor: isDark ? "rgba(16, 185, 129, 0.16)" : "#dcfce7",
                borderTop: `2px solid ${isDark ? "#10b981" : "#86efac"}`,
              }}
            >
              <TableCell
                colSpan={3}
                sx={{
                  textAlign: "left",
                  py: 2.2,
                  pl: 3,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontSize: "15px",
                    color: isDark ? "#6ee7b7" : "#15803d",
                  }}
                >
                  {t("Total, top 5")}
                </Typography>
              </TableCell>

              {/* % GHG Reduction total */}
              <TableCell sx={{ textAlign: "right", py: 2.2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    fontSize: "14px",
                    color: isDark ? "#6ee7b7" : "#15803d",
                  }}
                >
                  {summary.totalPctReductionStr || summary.total_pct_reduction_str || "—"}
                </Typography>
              </TableCell>

              {/* Total Min Reduction (tCO2e) */}
              <TableCell sx={{ textAlign: "right", py: 2.2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontSize: "15px",
                    color: isDark ? "#6ee7b7" : "#15803d",
                  }}
                >
                  {(summary.totalMinReductionTco2e ?? summary.total_min_reduction_tco2e ?? 0).toLocaleString()}
                </Typography>
              </TableCell>

              {/* Total Max Reduction (tCO2e) */}
              <TableCell sx={{ textAlign: "right", py: 2.2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontSize: "15px",
                    color: isDark ? "#6ee7b7" : "#15803d",
                  }}
                >
                  {(summary.totalMaxReductionTco2e ?? summary.total_max_reduction_tco2e ?? 0).toLocaleString()}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ActionableLeversTable;

